import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  MentionCollaboratorItem,
  MentionBotItem,
  MentionGroupItem,
  MentionItem,
  MentionUserItem,
} from './types';
import './MentionDropdown.less';
import {
  Global01NavQunzunLineIcon,
  Global01NavWodeLineIcon,
  Global02FunctionBotLineIcon,
  Global03ActionSousuonewLineIcon,
} from '@kim/icon';
import { Icon } from '@docs/kd-d2c-components';
import { eed_mario_icon_faker_line } from '@/assets/icons';

export type {
  MentionCollaboratorItem,
  MentionBotItem,
  MentionGroupItem,
  MentionItem,
  MentionUserItem,
};

interface Props {
  items: MentionItem;
  query: string;
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  activeIndex: number;
  /** 与 useH5ClawMention 一致：left 为弹窗左缘；bottom 为 position:fixed 的 bottom（距视口底部像素） */
  anchorRect: { left: number; top: number; bottom: number };
  onSelect: (
    item:
      | MentionUserItem
      | MentionGroupItem
      | MentionBotItem
      | MentionCollaboratorItem
  ) => void;
  onLoadMore?: () => void;
  showCollaboratorTab?: boolean;
}

const EDGE_MARGIN = 8;

const MentionDropdown: React.FC<Props> = ({
  items,
  query,
  loading,
  loadingMore = false,
  hasMore = false,
  activeIndex,
  anchorRect,
  onSelect,
  onLoadMore,
  showCollaboratorTab = false,
}) => {
  const { user: users, group: groups, bot: bots, collaborator: collaborators } = items;
  const trimmedQuery = query.trim();
  const hasQuery = trimmedQuery.length > 0;
  const hasResults =
    users.length > 0 || groups.length > 0 || bots.length > 0 || collaborators.length > 0;
  /** 搜索 Tab：与接口约定一致，先 USER 再 GROUP 再 MSG_BOT */
  const searchTabFlatItems: (
    | MentionUserItem
    | MentionCollaboratorItem
    | MentionGroupItem
    | MentionBotItem
  )[] = [...users, ...collaborators, ...groups, ...bots];
  /** 人 / 群 / 搜索群组 三个 Tab 均无列表数据 */
  const noListResults =
    users.length === 0 && groups.length === 0 && bots.length === 0 && collaborators.length === 0;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeItemRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<
    'search' | 'user' | 'collaborator' | 'group' | 'bot'
  >('search');

  /** 同步用 ref：不能把 activeTab 放进下方 effect 依赖，否则每次手动切 Tab 都会触发并按 activeIndex 强行改回「用户」等 */
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  /** 仅无结果或加载中的搜索态用固定矮高度；有列表时用与分类 Tab 相同可滚动区域 */
  const searchTabCompact =
    activeTab === 'search' && (loading || searchTabFlatItems.length === 0);

  useEffect(() => {
    if (loading || !hasResults) return;
    if (activeTabRef.current === 'search') return;
    const u = users.length;
    const c = collaborators.length;
    const g = groups.length;
    if (activeIndex < u) {
      setActiveTab('user');
    } else if (activeIndex < u + c) {
      setActiveTab('collaborator');
    } else if (activeIndex < u + c + g) {
      setActiveTab('group');
    } else {
      setActiveTab('bot');
    }
  }, [activeIndex, hasResults, loading, users.length, collaborators.length, groups.length]);

  /** 与 useH5ClawMention 约定一致：`bottom` 为 CSS fixed 的 bottom（距视口底），非 @ 字符的 viewport Y */
  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    left: anchorRect.left,
    bottom: anchorRect.bottom,
    visibility: 'hidden',
    zIndex: 9999,
  });

  useLayoutEffect(() => {
    const el = dropdownRef.current;
    if (!el) return;

    const { width, height } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = anchorRect.left;
    if (left + width > vw - EDGE_MARGIN) {
      left = Math.max(EDGE_MARGIN, vw - width - EDGE_MARGIN);
    }

    let bottom = anchorRect.bottom;
    const topSpace = vh - bottom - height;
    if (topSpace < EDGE_MARGIN) {
      bottom = Math.max(EDGE_MARGIN, vh - height - EDGE_MARGIN);
    }

    setStyle({
      position: 'fixed',
      left,
      bottom,
      zIndex: 9999,
      visibility: 'visible',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRect.left, anchorRect.bottom, items, loading]);

  useLayoutEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, activeTab]);

  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    const container = dropdownRef.current;
    if (!container || !hasMore) return;

    const contentEl = container.querySelector(
      '.mention-dropdown-content',
    ) as HTMLElement | null;
    if (!contentEl) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = contentEl;
      if (scrollHeight - scrollTop - clientHeight < 30) {
        onLoadMoreRef.current?.();
      }
    };

    contentEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => contentEl.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, activeTab]);

  const userActiveIdx =
    activeTab === 'user' && activeIndex < users.length ? activeIndex : -1;
  const groupActiveIdx =
    activeTab === 'group' &&
      activeIndex >= users.length + collaborators.length &&
      activeIndex < users.length + collaborators.length + groups.length
      ? activeIndex - users.length - collaborators.length
      : -1;
  const botActiveIdx =
    activeTab === 'bot' &&
    activeIndex >= users.length + collaborators.length + groups.length
      ? activeIndex - users.length - collaborators.length - groups.length
      : -1;
  const collaboratorActiveIdx =
    activeTab === 'collaborator' &&
    activeIndex >= users.length &&
    activeIndex < users.length + collaborators.length
      ? activeIndex - users.length
      : -1;

  const content = (
    <div
      ref={dropdownRef}
      className={`mention-dropdown${searchTabCompact ? ' mention-dropdown--search' : ''}`}
      style={style}
    >
      <div className="mention-dropdown-wrapper">
        {activeTab === 'search' && (
          <div className="mention-dropdown-content mention-dropdown-content--search">
            {loading ? (
              <div className="mention-dropdown-skeletons">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div className="mention-dropdown-skeleton" key={idx}>
                    <div className="mention-dropdown-skeleton-circle" />
                    <div className="mention-dropdown-skeleton-lines">
                      <div className="mention-dropdown-skeleton-line mention-dropdown-skeleton-line--long" />
                      <div className="mention-dropdown-skeleton-line mention-dropdown-skeleton-line--short" />
                    </div>
                  </div>
                ))}
              </div>
            ) : searchTabFlatItems.length > 0 ? (
              <>
                {searchTabFlatItems.map((item, idx) => (
                  <div
                    key={`${item.type}-${item.id}`}
                    ref={idx === activeIndex ? activeItemRef : undefined}
                    className={`mention-dropdown-item${idx === activeIndex ? ' mention-dropdown-item--active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(item);
                    }}
                  >
                    {item.avatar ? (
                      <img
                        className="mention-dropdown-avatar"
                        src={item.avatar}
                        alt=""
                      />
                    ) : (
                      <div className="mention-dropdown-avatar mention-dropdown-avatar--placeholder">
                        {item.name.slice(0, 1)}
                      </div>
                    )}
                    <div className="mention-dropdown-info">
                      <span className="mention-dropdown-name">
                        {item.name}
                      </span>
                      {'desc' in item && item.desc ? (
                        <span className="mention-dropdown-desc">
                          {item.desc}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}
                {loadingMore && (
                  <div className="mention-dropdown-loading">加载中…</div>
                )}
              </>
            ) : (
              <div className="mention-dropdown-status">
                {hasQuery && noListResults
                  ? '暂无搜索结果'
                  : showCollaboratorTab ? '请继续输入用户名称、协作者名称、群聊名称或消息号名称' : '请继续输入用户名称、群聊名称或消息号名称'}
              </div>
            )}
          </div>
        )}

        {activeTab === 'user' && (
          <div className="mention-dropdown-content">
            {users.length === 0 ? (
              <div className="mention-dropdown-empty">暂无用户</div>
            ) : (
              <>
                {users.map((item, idx) => (
                  <div
                    key={item.id}
                    ref={idx === userActiveIdx ? activeItemRef : undefined}
                    className={`mention-dropdown-item${idx === userActiveIdx ? ' mention-dropdown-item--active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(item);
                    }}
                  >
                    {item.avatar ? (
                      <img
                        className="mention-dropdown-avatar"
                        src={item.avatar}
                        alt=""
                      />
                    ) : (
                      <div className="mention-dropdown-avatar mention-dropdown-avatar--placeholder">
                        {item.name.slice(0, 1)}
                      </div>
                    )}
                    <div className="mention-dropdown-info">
                      <span className="mention-dropdown-name">
                        {item.name}
                      </span>
                      {item.desc ? (
                        <span className="mention-dropdown-desc">
                          {item.desc}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}
                {loadingMore && (
                  <div className="mention-dropdown-loading">加载中…</div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'group' && (
          <div className="mention-dropdown-content">
            {groups.length === 0 ? (
              <div className="mention-dropdown-empty">暂无群组</div>
            ) : (
              <>
                {groups.map((item, idx) => (
                  <div
                    key={item.id}
                    ref={idx === groupActiveIdx ? activeItemRef : undefined}
                    className={`mention-dropdown-item${idx === groupActiveIdx ? ' mention-dropdown-item--active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(item);
                    }}
                  >
                    {item.avatar ? (
                      <img
                        className="mention-dropdown-avatar"
                        src={item.avatar}
                        alt=""
                      />
                    ) : (
                      <div className="mention-dropdown-avatar mention-dropdown-avatar--placeholder">
                        {item.name.slice(0, 1)}
                      </div>
                    )}
                    <div className="mention-dropdown-info">
                      <span className="mention-dropdown-name">
                        {item.name}
                      </span>
                    </div>
                  </div>
                ))}
                {loadingMore && (
                  <div className="mention-dropdown-loading">加载中…</div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'collaborator' && showCollaboratorTab && (
          <div className="mention-dropdown-content">
            {collaborators.length === 0 ? (
              <div className="mention-dropdown-empty">暂无协作者</div>
            ) : (
              <>
                {collaborators.map((item, idx) => (
                  <div
                    key={item.id}
                    ref={idx === collaboratorActiveIdx ? activeItemRef : undefined}
                    className={`mention-dropdown-item${idx === collaboratorActiveIdx ? ' mention-dropdown-item--active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(item);
                    }}
                  >
                    {item.avatar ? (
                      <img
                        className="mention-dropdown-avatar"
                        src={item.avatar}
                        alt=""
                      />
                    ) : (
                      <div className="mention-dropdown-avatar mention-dropdown-avatar--placeholder">
                        {item.name.slice(0, 1)}
                      </div>
                    )}
                    <div className="mention-dropdown-info">
                      <span className="mention-dropdown-name">
                        {item.name}
                      </span>
                      {item.desc ? (
                        <span className="mention-dropdown-desc">
                          {item.desc}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {activeTab === 'bot' && (
          <div className="mention-dropdown-content">
            {bots.length === 0 ? (
              <div className="mention-dropdown-empty">暂无消息号</div>
            ) : (
              <>
                {bots.map((item, idx) => (
                  <div
                    key={item.id}
                    ref={idx === botActiveIdx ? activeItemRef : undefined}
                    className={`mention-dropdown-item${idx === botActiveIdx ? ' mention-dropdown-item--active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSelect(item);
                    }}
                  >
                    {item.avatar ? (
                      <img
                        className="mention-dropdown-avatar"
                        src={item.avatar}
                        alt=""
                      />
                    ) : (
                      <div className="mention-dropdown-avatar mention-dropdown-avatar--placeholder">
                        {item.name.slice(0, 1)}
                      </div>
                    )}
                    <div className="mention-dropdown-info">
                      <span className="mention-dropdown-name">
                        {item.name}
                      </span>
                      {item.desc ? (
                        <span className="mention-dropdown-desc">
                          {item.desc}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ))}
                {loadingMore && (
                  <div className="mention-dropdown-loading">加载中…</div>
                )}
              </>
            )}
          </div>
        )}

        <div className="mention-dropdown-tabs">
          <div
            className={`mention-dropdown-tab${activeTab === 'search' ? ' mention-dropdown-tab--active' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault();
              setActiveTab('search');
            }}
          >
            <Global03ActionSousuonewLineIcon size={20} />
          </div>
          <div
            className={`mention-dropdown-tab${activeTab === 'user' ? ' mention-dropdown-tab--active' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault();
              setActiveTab('user');
            }}
            title="用户"
          >
            <Global01NavWodeLineIcon size={20} />
          </div>
          <div
            className={`mention-dropdown-tab${activeTab === 'group' ? ' mention-dropdown-tab--active' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault();
              setActiveTab('group');
            }}
            title="群组"
          >
            <Global01NavQunzunLineIcon size={20} />
          </div>
          <div
            className={`mention-dropdown-tab${activeTab === 'bot' ? ' mention-dropdown-tab--active' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault();
              setActiveTab('bot');
            }}
            title="搜索群组"
          >
            <Global02FunctionBotLineIcon size={20} />
          </div>
          {showCollaboratorTab && (
            <div
              className={`mention-dropdown-tab${activeTab === 'collaborator' ? ' mention-dropdown-tab--active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setActiveTab('collaborator');
              }}
              title="协作者"
            >
              <Icon icon={eed_mario_icon_faker_line} size={24} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default MentionDropdown;
